import { IFilterOptions, ITradeDataRegion } from '../../Types/types';
import { ALL, EVP_SE, EVP_SET, IPU_EU, PIPELINE_GAS, POWER, POWER_INTENSITY_MODEL, SE_GLOBAL_SBTI } from '../../api/constants';
import {
  GAS_COMMODITY_ORDER,
  GAS_DISCLAIMER,
  hardCodedValueForSet2024Emission,
  hardCodedValueForSet2024Volume,
  hardCodedValueForSetIpuRegionLevel2023Emission,
  hardCodedValueForSetIpuRegionLevel2023Volume,
  hardCodedValueForSetIpuRegionLevel2024Emission,
  hardCodedValueForSetIpuRegionLevel2024Volume,
  pipeLineGas,
  PLANNING_ONE_SOURCE,
  POWER_DISCLAIMER,
  regionOptions,
  SHELL_ENERGY_TRADING,
} from '../../constants';
import { IShowEvpData } from '../../redux/dashboard/dashboard.types';
import { currentYear } from '../../utils/utils';
import { SummedValues, TradeData } from '../charts/CarbonIntensityChart/types';
import { ProcessedDataItem } from '../charts/VolumeCarbonEmissionChart/types';

//filter target data by region
export const filterTargetDataByRegion = (
  region: string,
  carbonIntensityTarget: ITradeDataRegion[],
) => {
  const carbonIntensityData = carbonIntensityTarget;
  let firstFilteredData = [];
  if (Array.isArray(carbonIntensityData)) {
    const filteredData = carbonIntensityData.filter((item) => {
      return item.REGION.includes(region);
    });
    if (filteredData.length > 0) {
      firstFilteredData = [
        {
          year: filteredData[0].TRADE_YEAR.toString(),
          type: 'Target',
          total_value: filteredData[0].TARGET_VALUE,
          region: region === SE_GLOBAL_SBTI ? 'Global' : region,
        },
      ];
    } else {
      firstFilteredData = [
        {
          year: 2030,
          type: 'Target',
          total_value: 0,
          region: region === SE_GLOBAL_SBTI ? 'Global' : region,
        },
      ];
    }
  } else {
    firstFilteredData = [{ year: '0', type: 'Target', total_value: 0 }];
  }
  return firstFilteredData;
};

export const getFilterOptions = (data: IFilterOptions[], key: keyof IFilterOptions): string[] => {
  return data
    .flatMap((val) => (val[key] !== null ? String([val[key]]) : []))
    .filter((v, index, self) => self.indexOf(v) === index);
};

export const getRegionFromSeIpu = (seIpu: string) => {
  const index = regionOptions.findIndex((item) => seIpu.includes(item));
  return index === 0 ? SE_GLOBAL_SBTI : regionOptions[index]?.toUpperCase();
};

export const getRename = (name: string) => {
  if (
    ['External Gas Sales Volumes (Pipeline)', 'Inland Sales Volumes - Pipeline Gas'].includes(name)
  ) {
    return 'Pipeline Gas';
  } else if (name === 'External Gas Sales Volumes (Renewable)') {
    return 'Renewable Gas';
  } else {
    return name;
  }
};

export const getTransformSource = (data: string): string => {
  const sourceArray = data?.split(',').map((item) => item.trim());
  const isPowerIntensityModelAndTanso = [POWER_INTENSITY_MODEL, 'Tanso'].every((item) =>
    sourceArray.includes(item),
  );
  const updatedData = Array.from(
    new Set(
      sourceArray.map((item) => {
        if (item.includes('LE_SE') || item.includes('LE_SET')) {
          return 'Reported Latest Estimate';
        } else if (item.trim() === 'Tanso') {
          return 'Approved SET Snapshots';
        } else if (item.trim() === 'Tanso_Future') {
          return 'Booked_Futures';
        } else if (item.trim() === POWER_INTENSITY_MODEL) {
          return `${POWER_INTENSITY_MODEL}${!isPowerIntensityModelAndTanso ? ',Approved SET Snapshots' : ''
            }`;
        } else if (item.trim() === PLANNING_ONE_SOURCE) {
          return 'Operating Plan';
        }
        return item;
      }),
    ),
  );
  const transformSource = updatedData.flatMap((item) => item.split(',')).map((item) => item.trim());
  return transformSource.join(', ');
};

export const mergeAndSumPipelineGas = (processedCombinedArrayWithKey: SummedValues[]) => {
  const mergedCurrentYearPlanAndActualData = { actual: 0, plan: 0 };
  const pipelineGasEntries = processedCombinedArrayWithKey.filter(
    (item) => item.name === pipeLineGas,
  );

  if (pipelineGasEntries.length < 2) {
    pipelineGasEntries.forEach((item) => {
      if (item.currentYearPlanAndActualData) {
        mergedCurrentYearPlanAndActualData.actual += item.currentYearPlanAndActualData.actual;
        mergedCurrentYearPlanAndActualData.plan += item.currentYearPlanAndActualData.plan;
      }
    });
    return processedCombinedArrayWithKey;
  }

  const mergedPipelineGasMap = new Map<string, ProcessedDataItem>();

  const addToMap = (entry: ProcessedDataItem) => {
    if (mergedPipelineGasMap.has(entry.year)) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const existingEntry = mergedPipelineGasMap.get(entry.year)!;
      existingEntry.value += entry.value;
    } else {
      mergedPipelineGasMap.set(entry.year, { ...entry });
    }
  };

  pipelineGasEntries.forEach((item) => {
    item.data.forEach(addToMap);
    if (item.currentYearPlanAndActualData) {
      mergedCurrentYearPlanAndActualData.actual += item.currentYearPlanAndActualData.actual;
      mergedCurrentYearPlanAndActualData.plan += item.currentYearPlanAndActualData.plan;
    }
  });

  const mergedPipelineGasData = Array.from(mergedPipelineGasMap.values());
  const renewableGasData = processedCombinedArrayWithKey.filter(
    (item) => item.name !== pipeLineGas,
  );
  return renewableGasData.concat([
    {
      name: pipeLineGas,
      data: mergedPipelineGasData,
      currentYearPlanAndActualData: mergedCurrentYearPlanAndActualData,
    },
  ]);
};

// setEvpValue - this function checks user's eligibility to see SE or SET data and set the EVP field value accordingly
export const setEvpValue = (options: string[], showEvpData: IShowEvpData) => {
  if (showEvpData.showAllData) {
    return ALL;
  } else if (showEvpData.showSeData) {
    return options.find((item: string) => item.toUpperCase().includes(EVP_SE.toUpperCase())) ?? ALL;
  } else if (showEvpData.showSetData) {
    return (
      options.find((item: string) => item.toUpperCase().includes(EVP_SET.toUpperCase())) ?? ALL
    );
  }
  return ALL;
};

export const getCommodityVal = (commodityOverviewApi: TradeData[]) => {
  return [...new Set(commodityOverviewApi.map((val: TradeData) => val.TRADE_COMMODITY_NAME))].sort(
    (commodity1, commodity2) => {
      const order = ['power', 'Pipeline Gas'];
      return order.indexOf(commodity1) - order.indexOf(commodity2);
    },
  );
};

export const getOrderInCommodityPage = (updatedGroupedData: SummedValues[]) => {
  return updatedGroupedData.sort((a, b) => {
    return GAS_COMMODITY_ORDER.indexOf(a.name) - GAS_COMMODITY_ORDER.indexOf(b.name);
  });
};

export const getActualLastYearIndex = (
  resultYearArray: { year: string | number; type: string }[]
) => {
  let lastIndex = -1;
  resultYearArray.forEach((item, index) => {
    const isActuals = item.type.replace('historical', 'actuals') === 'actuals';
    const isNotCurrentYear = String(item.year) !== String(currentYear);
    if (isActuals && isNotCurrentYear) {
      lastIndex = index;
    }
  });
  return lastIndex;
};

export const updateResRenValue = ({chartType, name, evp, seIpuRegionId, reportingEntity, value, year, commodityVal}: {chartType: string, name: string, evp: string, seIpuRegionId: string, reportingEntity: string, value: number, year: number, commodityVal: string}) => {
  const year2024 = 2024;
  const year2023 = 2023;
  const hardCodedValueForSet2024RegionLevel = chartType === 'sales' ? hardCodedValueForSetIpuRegionLevel2024Volume : hardCodedValueForSetIpuRegionLevel2024Emission;
  const hardCodedValueForSet2023RegionLevel = chartType === 'sales' ? hardCodedValueForSetIpuRegionLevel2023Volume : hardCodedValueForSetIpuRegionLevel2023Emission;
  const hardCodedData = chartType === 'sales' ? hardCodedValueForSet2024Volume : hardCodedValueForSet2024Emission;
  const isCommodityEvpSeIpu = commodityVal === 'Power' && evp === `T&S ${SHELL_ENERGY_TRADING}` && seIpuRegionId === `SE ${IPU_EU}`;

  if (isCommodityEvpSeIpu && (year === year2024 || year === year2023) && !reportingEntity) {
    const regionData = year === year2024 ? hardCodedValueForSet2024RegionLevel : hardCodedValueForSet2023RegionLevel;
    return regionData[name as keyof typeof regionData];
  } else {
    const foundItem = hardCodedData.find(item => item.reportingEntity === reportingEntity);
    if (foundItem && year === year2024) {
      return Number(foundItem[name as keyof typeof foundItem]);
    } else {
      return Math.max(value, 0.0001); // making negative value to 0.0001
    }
  }
}

export const getDisclaimer = (
  commodityParamVal: string,
  evpParamVal: string,
  disclaimerForPower?: string[] | string,
  disclaimerForGas?: string[] | string,
) => {
  const powerDisclaimer = disclaimerForPower ?? POWER_DISCLAIMER;
  const gasDisclaimer = disclaimerForGas ?? GAS_DISCLAIMER;
  if (commodityParamVal === PIPELINE_GAS) {
    return gasDisclaimer;
  }
  return commodityParamVal === POWER && evpParamVal !== EVP_SE ? powerDisclaimer : null;
};
